import {
  forwardRef,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type ComponentPropsWithoutRef,
  type ForwardRefExoticComponent,
  type ReactNode,
  type RefAttributes,
} from "react";
import type { JSX } from "react";
import { cn } from "../ui";
import {
  shouldSkipWindowDragStart,
  startWindowDrag,
} from "../../tauri/windowDrag";

export type PaneTone = "surface" | "surface-secondary" | "surface-tertiary";
type PaneInset = "none" | "md" | "lg";

function toneClass(tone: PaneTone): string {
  if (tone === "surface-secondary") return "bg-surface-secondary";
  if (tone === "surface-tertiary") return "bg-surface-tertiary";
  return "bg-surface";
}

function insetClass(inset: PaneInset): string {
  if (inset === "lg") return "px-6";
  if (inset === "md") return "px-4";
  return "";
}

export interface PaneFrameProps {
  readonly children: ReactNode;
  readonly className?: string;
  readonly tone?: PaneTone;
  readonly style?: CSSProperties;
  readonly withRightBorder?: boolean;
}

export function PaneFrame({
  children,
  className,
  tone = "surface-tertiary",
  style,
  withRightBorder = false,
}: PaneFrameProps): JSX.Element {
  return (
    <div
      style={style}
      className={cn(
        "flex h-full min-h-0 flex-col",
        toneClass(tone),
        withRightBorder ? "border-r border-edge" : "",
        className,
      )}
    >
      {children}
    </div>
  );
}

export interface PaneHeaderBarProps {
  readonly children: ReactNode;
  readonly className?: string;
  readonly tone?: PaneTone;
  readonly inset?: PaneInset;
  readonly withBottomBorder?: boolean;
}

export function PaneHeaderBar({
  children,
  className,
  tone = "surface-tertiary",
  inset = "md",
  withBottomBorder = true,
}: PaneHeaderBarProps): JSX.Element {
  const handleMouseDown = (event: ReactMouseEvent<HTMLElement>): void => {
    if (event.button !== 0 || shouldSkipWindowDragStart(event.target)) {
      return;
    }
    startWindowDrag();
  };

  return (
    <header
      data-tauri-drag-region
      onMouseDown={handleMouseDown}
      className={cn(
        "shrink-0 min-h-[52px] py-3 flex items-center cursor-default select-none",
        toneClass(tone),
        insetClass(inset),
        withBottomBorder ? "border-b border-edge" : "",
        className,
      )}
    >
      {children}
    </header>
  );
}


export interface PaneContentProps extends Omit<
  ComponentPropsWithoutRef<"div">,
  "children" | "className"
> {
  readonly children: ReactNode;
  readonly className?: string;
  readonly scrollY?: boolean;
}

export const PaneContent: ForwardRefExoticComponent<
  PaneContentProps & RefAttributes<HTMLDivElement>
> = forwardRef<HTMLDivElement, PaneContentProps>(function PaneContent(
  { children, className, scrollY = true, ...rest }: PaneContentProps,
  ref,
): JSX.Element {
  return (
    <div
      ref={ref}
      className={cn(
        "flex-1 min-h-0",
        scrollY ? "overflow-y-auto overflow-x-hidden" : "",
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
});

export interface PaneFooterBarProps {
  readonly children: ReactNode;
  readonly className?: string;
  readonly tone?: PaneTone;
  readonly inset?: PaneInset;
  readonly withTopBorder?: boolean;
  readonly compact?: boolean;
}

export function PaneFooterBar({
  children,
  className,
  tone = "surface-tertiary",
  inset = "md",
  withTopBorder = true,
  compact = true,
}: PaneFooterBarProps): JSX.Element {
  return (
    <footer
      className={cn(
        "shrink-0 flex items-center",
        compact ? "py-2.5" : "h-[52px]",
        toneClass(tone),
        insetClass(inset),
        withTopBorder ? "border-t border-edge" : "",
        className,
      )}
    >
      {children}
    </footer>
  );
}
